import {
  ReactNode,
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  AnalyticsEvent,
  AnalyticsParent,
  TrackPayload,
  useAnalyticsTrack,
} from "@repo/edtech-analytics";
import { revalidateCourse } from "@repo/edtech-api";

import { LessonType } from "../../types";
import { useIdContext } from "../id-provider";
import { useMainContextValues } from "../main-provider";
import { AnalyticsContext } from "./analytics-context";
import { LESSON_TO_ANALYTICS } from "./config";

type Props = {
  children: ReactNode,
  analyticEventsCache: RefObject<Set<string>>,
  isAutomaticFinishLesson: boolean,
};

type PendingParams = {
  params: TrackPayload,
  dedup: boolean,
  shouldUpdate?: boolean,
};

export const AnalyticsProvider = ({
  children,
  analyticEventsCache,
  isAutomaticFinishLesson,
}: Props) => {
  const {
    publicationId,
    unitId: mainUnitId,
  } = useIdContext();
  const { currentLesson } = useMainContextValues();

  const [ pendingLessons, setPendingLessons ] = useState<Map<string, PendingParams>>(new Map());

  const analyticsParent = useMemo(() => {
    return (
      LESSON_TO_ANALYTICS[ currentLesson?.materialType as LessonType ] ??
      AnalyticsParent.Test
    );
  }, [ currentLesson?.materialType ]);

  const materialUid = currentLesson?.uid ?? "";
  const materialNodeId = currentLesson?.nodeId ?? "";

  const analTrigger = useAnalyticsTrack(analyticEventsCache);

  const trackImpl = useCallback(async (
    params: TrackPayload,
    dedup = false,
    shouldUpdate?: boolean,
  ) => {

    await analTrigger({
      dedup,
      publicationId,
      unitId: currentLesson?.unitParentId ?? "",
      materialId: materialUid,
      parentId: analyticsParent,
      params,
    });

    if (shouldUpdate) {
      await revalidateCourse(publicationId, mainUnitId);
    }
  },
  [
    publicationId,
    currentLesson?.unitParentId,
    currentLesson?.uid,
    currentLesson?.nodeId,
    analyticEventsCache,
    analTrigger,
    materialUid,
    analyticsParent,
    mainUnitId,
  ]);

  const onFinishLessonImpl = useCallback((
    params: TrackPayload,
    dedup = false,
    shouldUpdate?: boolean,
  ) => {
    const nodeId = currentLesson?.nodeId;

    if (!nodeId) return;

    const shouldDefer = !isAutomaticFinishLesson && params.type === AnalyticsEvent.Completed;

    if (!shouldDefer) {
      trackImpl(
        params, dedup, shouldUpdate,
      );
      return;
    }

    setPendingLessons((prev) => {
      const next = new Map(prev);
      next.set(nodeId, {
        params,
        dedup,
        shouldUpdate,
      });
      return next;
    });
  }, [
    currentLesson?.nodeId,
    isAutomaticFinishLesson,
    trackImpl,
  ]);

  const submitPendingFinishImpl = useCallback(async () => {
    const nodeId = currentLesson?.nodeId;
    if (!nodeId) return;

    const pending = pendingLessons.get(nodeId);
    if (pending) {
      await trackImpl(
        pending.params, pending.dedup, pending.shouldUpdate,
      );
      setPendingLessons((prev) => {
        const next = new Map(prev);
        next.delete(nodeId);
        return next;
      });
    }
  }, [
    currentLesson?.nodeId,
    pendingLessons,
    trackImpl,
  ]);

  const isReadyToComplete = useMemo(() => {
    if (!materialNodeId) return false;
    return pendingLessons.has(materialNodeId);
  }, [ materialNodeId, pendingLessons ]);

  const trackRef = useRef(trackImpl);
  const onFinishLessonRef = useRef(onFinishLessonImpl);
  const submitPendingFinishRef = useRef(submitPendingFinishImpl);

  trackRef.current = trackImpl;
  onFinishLessonRef.current = onFinishLessonImpl;
  submitPendingFinishRef.current = submitPendingFinishImpl;

  const track = useCallback((
    params: TrackPayload,
    dedup?: boolean,
    shouldUpdate?: boolean,
  ) => {
    return trackRef.current(
      params, dedup, shouldUpdate,
    );
  }, []);

  const onFinishLesson = useCallback((
    params: TrackPayload,
    dedup?: boolean,
    shouldUpdate?: boolean,
  ) => {
    return onFinishLessonRef.current(
      params, dedup, shouldUpdate,
    );
  }, []);

  const submitPendingFinish = useCallback(async () => {
    return submitPendingFinishRef.current();
  }, []);

  const value = useMemo(() => ({
    track,
    onFinishLesson,
    submitPendingFinish,
    isReadyToComplete,
  }), [
    track,
    onFinishLesson,
    submitPendingFinish,
    isReadyToComplete,
  ]);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
